import { useEffect, useMemo, useState } from "react";
import api, { API } from "../../lib/api";
import {
  ChartBar, Download, ArrowsClockwise, CurrencyDollar, Users, Ticket, Buildings,
} from "@phosphor-icons/react";

// ---------- helpers ----------
const fmtMoney = (n, c = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: c, maximumFractionDigits: 2 }).format(n || 0);
const fmtInt = (n) => new Intl.NumberFormat("en-US").format(n || 0);
const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;

function todayLocal() {
  // YYYY-MM-DD in local time (matches the backend's America/New_York default)
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function janFirst() {
  const y = new Date().getFullYear();
  return `${y}-01-01`;
}
function nDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

const PRESETS = [
  { key: "today", label: "Today", start: todayLocal, end: todayLocal },
  { key: "mtd", label: "Month to date", start: firstOfMonth, end: todayLocal },
  { key: "30", label: "Last 30 days", start: () => nDaysAgo(30), end: todayLocal },
  { key: "90", label: "Last 90 days", start: () => nDaysAgo(90), end: todayLocal },
  { key: "ytd", label: "Year to date", start: janFirst, end: todayLocal },
  { key: "all", label: "All time", start: () => "2024-01-01", end: todayLocal },
];

// ---------- main ----------
export default function AdminStats() {
  const [start, setStart] = useState(janFirst());
  const [end, setEnd] = useState(todayLocal());
  const [activePreset, setActivePreset] = useState("ytd");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async (s, e) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stats/ops", { params: { start: s, end: e } });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(start, end); }, []); // eslint-disable-line

  const applyPreset = (p) => {
    const s = p.start();
    const e = p.end();
    setStart(s); setEnd(e);
    setActivePreset(p.key);
    fetchStats(s, e);
  };

  const onCustomApply = () => {
    setActivePreset("custom");
    fetchStats(start, end);
  };

  const downloadCsv = (kind) => {
    const token = localStorage.getItem("ms_admin_token");
    const url = `${API}/admin/stats/ops/export/${kind}.csv?start=${start}&end=${end}`;
    // We can't set headers on a window.open, so use fetch → blob → download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `mystery-served_${kind}_${start}_to_${end}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <div className="p-5 md:p-12" data-testid="admin-ops-stats">
      <p className="label-eyebrow text-ms-stone">Operations</p>
      <h1 className="serif text-3xl md:text-4xl text-ms-earth mt-2 flex items-center gap-3">
        <ChartBar size={32} weight="duotone" className="text-ms-brass" /> Stats
      </h1>
      <p className="mt-2 text-sm text-ms-stone max-w-2xl">
        Revenue, bookings, capacity, tour mix, and customer metrics for any date range.
        Default view is Year to Date. Export bookings detail or aggregate summary as CSV for accounting / grant applications.
      </p>

      {/* Range controls */}
      <section className="mt-6 bg-white border border-ms-edge rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-2" data-testid="stats-presets">
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-all ${activePreset === p.key ? "bg-ms-earth text-ms-cream border-ms-earth" : "border-ms-edge text-ms-stone hover:bg-ms-cream"}`}
              data-testid={`stats-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col">
            <span className="label-eyebrow text-ms-stone text-[10px]">From</span>
            <input
              type="date"
              value={start}
              onChange={e => { setStart(e.target.value); setActivePreset("custom"); }}
              className="mt-1 border border-ms-edge rounded-md px-3 py-2 text-sm"
              data-testid="stats-start-date"
            />
          </label>
          <label className="flex flex-col">
            <span className="label-eyebrow text-ms-stone text-[10px]">To</span>
            <input
              type="date"
              value={end}
              onChange={e => { setEnd(e.target.value); setActivePreset("custom"); }}
              className="mt-1 border border-ms-edge rounded-md px-3 py-2 text-sm"
              data-testid="stats-end-date"
            />
          </label>
          <button
            onClick={onCustomApply}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow border border-ms-earth text-ms-earth rounded-md hover:bg-ms-earth hover:text-ms-cream transition-colors"
            data-testid="stats-apply"
          >
            <ArrowsClockwise size={12} /> Apply
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => downloadCsv("bookings")}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow border border-ms-edge text-ms-earth rounded-md hover:bg-ms-cream transition-colors"
              data-testid="stats-export-bookings"
            >
              <Download size={12} /> Bookings CSV
            </button>
            <button
              onClick={() => downloadCsv("summary")}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow bg-ms-earth text-ms-cream rounded-md hover:bg-ms-brass transition-colors"
              data-testid="stats-export-summary"
            >
              <Download size={12} /> Summary CSV
            </button>
          </div>
        </div>
      </section>

      {loading || !data ? (
        <p className="mt-8 text-sm text-ms-stone">Loading stats…</p>
      ) : (
        <>
          {/* Revenue KPIs */}
          <section className="mt-8" data-testid="stats-revenue">
            <p className="label-eyebrow text-ms-stone text-xs">Revenue · {data.range.start} → {data.range.end}</p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="Net revenue (range)" value={fmtMoney(data.revenue.net)} accent="brass" sub={data.revenue.net < 0 ? "refunds exceed gross in range" : null} testid="kpi-net" />
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="Gross (range)" value={fmtMoney(data.revenue.gross)} testid="kpi-gross" />
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="Refunds (range)" value={fmtMoney(data.revenue.refunds)} testid="kpi-refunds" />
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="Today" value={fmtMoney(data.revenue.today)} testid="kpi-today" />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="YTD gross" value={fmtMoney(data.revenue.ytd_gross)} testid="kpi-ytd-gross" />
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="YTD net" value={fmtMoney(data.revenue.ytd_net)} testid="kpi-ytd-net" />
              <KpiCard icon={<Ticket size={18} weight="duotone" />} label="Bookings (range)" value={fmtInt(data.bookings.active)} sub={`${fmtInt(data.bookings.total_guests)} guests`} testid="kpi-bookings" />
              <KpiCard icon={<Ticket size={18} weight="duotone" />} label="Avg party" value={data.bookings.avg_party_size?.toFixed(2) || "—"} testid="kpi-avg-party" />
            </div>
          </section>

          {/* Capacity */}
          <section className="mt-10" data-testid="stats-capacity">
            <p className="label-eyebrow text-ms-stone text-xs">Capacity · {data.capacity.total_instances} instances in range</p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <CapacityCard
                label="Seats utilization"
                pct={data.capacity.seats_utilization_pct}
                detail={`${fmtInt(data.capacity.total_booked)} / ${fmtInt(data.capacity.total_capacity)} seats sold`}
                testid="cap-seats"
              />
              <CapacityCard
                label="Sell-out rate"
                pct={data.capacity.sellout_rate_pct}
                detail={`${fmtInt(data.capacity.sold_out_instances)} of ${fmtInt(data.capacity.total_instances)} instances fully booked`}
                testid="cap-sellout"
              />
            </div>
          </section>

          {/* Tour mix */}
          <section className="mt-10 bg-white border border-ms-edge rounded-2xl overflow-hidden" data-testid="stats-tour-mix">
            <div className="px-6 py-4 border-b border-ms-edge bg-ms-cream/30 flex items-center gap-2">
              <Buildings size={16} weight="duotone" className="text-ms-brass" />
              <p className="label-eyebrow text-ms-stone text-xs">Tour mix · revenue share</p>
            </div>
            {data.tour_mix.length === 0 ? (
              <p className="p-6 text-sm text-ms-stone">No completed bookings in this range.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-ms-cream/40 border-b border-ms-edge">
                  <tr>
                    <th className="text-left p-4 label-eyebrow text-[10px]">Tour</th>
                    <th className="text-right p-4 label-eyebrow text-[10px]">Bookings</th>
                    <th className="text-right p-4 label-eyebrow text-[10px]">Guests</th>
                    <th className="text-right p-4 label-eyebrow text-[10px]">Revenue</th>
                    <th className="text-left p-4 label-eyebrow text-[10px] w-1/3">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tour_mix.map(r => (
                    <tr key={r.tour_id} className="border-b border-ms-edge last:border-0" data-testid={`tour-mix-row-${r.tour_id}`}>
                      <td className="p-4 text-ms-earth">{r.title}</td>
                      <td className="p-4 text-right text-ms-earth">{fmtInt(r.bookings)}</td>
                      <td className="p-4 text-right text-ms-earth">{fmtInt(r.guests)}</td>
                      <td className="p-4 text-right text-ms-earth">{fmtMoney(r.revenue)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-ms-cream rounded overflow-hidden">
                            <div
                              className="h-full bg-ms-brass"
                              style={{ width: `${Math.min(100, r.share_pct)}%` }}
                            />
                          </div>
                          <span className="text-xs text-ms-stone w-12 text-right">{fmtPct(r.share_pct)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Customers */}
          <section className="mt-10" data-testid="stats-customers">
            <p className="label-eyebrow text-ms-stone text-xs">Customers</p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-4">
              <KpiCard icon={<Users size={18} weight="duotone" />} label="Total" value={fmtInt(data.customers.total)} testid="kpi-cust-total" />
              <KpiCard icon={<Users size={18} weight="duotone" />} label="New (range)" value={fmtInt(data.customers.new_in_range)} testid="kpi-cust-new" />
              <KpiCard icon={<Users size={18} weight="duotone" />} label="Repeat" value={fmtInt(data.customers.repeat)} sub={fmtPct(data.customers.repeat_rate_pct)} testid="kpi-cust-repeat" />
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="Avg LTV" value={fmtMoney(data.customers.avg_ltv)} testid="kpi-cust-ltv" />
              <KpiCard icon={<CurrencyDollar size={18} weight="duotone" />} label="Lifetime revenue" value={fmtMoney(data.customers.lifetime_revenue)} testid="kpi-cust-lifetime" />
            </div>
          </section>

          {/* Booking status breakdown */}
          <section className="mt-10 bg-white border border-ms-edge rounded-2xl p-6" data-testid="stats-status-breakdown">
            <p className="label-eyebrow text-ms-stone text-xs">Booking status (in range)</p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(data.bookings.by_status || {}).map(([k, v]) => (
                <div key={k} className="border border-ms-edge rounded-lg p-3" data-testid={`status-${k}`}>
                  <p className="text-[10px] uppercase tracking-wider text-ms-stone">{k}</p>
                  <p className="serif text-2xl text-ms-earth mt-1">{fmtInt(v)}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-ms-stone">
              Pending bookings = checkout started but never finalized. Refunded = full refund issued from admin or Stripe dashboard.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

// ---------- subcomponents ----------
function KpiCard({ icon, label, value, sub, accent, testid }) {
  const accentCls = accent === "brass" ? "border-ms-brass bg-ms-brass/5" : "border-ms-edge";
  return (
    <div className={`bg-white rounded-2xl p-5 border ${accentCls}`} data-testid={testid}>
      <p className="label-eyebrow text-ms-stone text-[10px] flex items-center gap-1.5">
        <span className="text-ms-brass">{icon}</span> {label}
      </p>
      <p className="serif text-3xl text-ms-earth mt-2">{value}</p>
      {sub && <p className="text-xs text-ms-stone mt-1">{sub}</p>}
    </div>
  );
}

function CapacityCard({ label, pct, detail, testid }) {
  const pctClamped = Math.min(100, Math.max(0, pct || 0));
  return (
    <div className="bg-white rounded-2xl p-6 border border-ms-edge" data-testid={testid}>
      <p className="label-eyebrow text-ms-stone text-xs">{label}</p>
      <div className="mt-3 flex items-baseline gap-2">
        <p className="serif text-4xl text-ms-earth">{fmtPct(pct)}</p>
      </div>
      <div className="mt-3 h-2 bg-ms-cream rounded overflow-hidden">
        <div className="h-full bg-ms-brass" style={{ width: `${pctClamped}%` }} />
      </div>
      <p className="mt-2 text-xs text-ms-stone">{detail}</p>
    </div>
  );
}
